import { useState, type ReactNode } from "react";
import { cn } from "../lib/cn";

interface TenantBrandProps {
  name: string;
  /** Third-party logo URL, or null when the tenant has none. */
  logoUrl: string | null;
  className?: string;
  /** Classes for the logo image, chiefly its height; the width follows the aspect ratio. */
  logoClassName?: string;
}

/**
 * A tenant's logo and name side by side, for the GPO shell header and the auth
 * card's branding block.
 *
 * The logo is a third-party URL: `referrerPolicy="no-referrer"` keeps the
 * portal host (and the path the user is on) out of the logo server's logs, and
 * a load error drops the image so the name alone identifies the tenant. The
 * image has an empty `alt` because the adjacent name is the accessible label.
 */
export function TenantBrand({
  name,
  logoUrl,
  className,
  logoClassName,
}: TenantBrandProps): ReactNode {
  const [failed, setFailed] = useState(false);
  const showLogo = logoUrl !== null && !failed;
  return (
    <span className={cn("flex items-center gap-3", className)}>
      {showLogo && (
        <img
          src={logoUrl}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
          className={cn("w-auto", logoClassName)}
        />
      )}
      <span className="font-semibold">{name}</span>
    </span>
  );
}
